import zod from "zod";
import {
    ZodAny,
    ZodArray,
    ZodIntersection,
    ZodNullable,
    ZodObject,
    ZodOptional,
    ZodRecord,
    ZodTypeAny,
    ZodUnion,
    ZodUnknown,
} from "zod";

import { Product } from "@dashboard/products/types";

import { osBuilderConfigSchema } from "./AdvanceModeSection.schema";

const productSchema = zod.looseObject({
    id: zod.number(),
    codename: zod.string(),
    groupId: zod.number().nullable(),
    nightlyPeriod: zod.number().min(0).nullable(),
    androidVersion: zod.string(),
    workspaceId: zod.custom<number>(),
    customBuildConfig: osBuilderConfigSchema.nullable(),
    isPublic: zod.boolean().optional(),
    changes: zod.record(zod.string(), zod.array(zod.string())).nullable().optional(),
    visibility: zod.array(zod.string()).optional(),
    actionFeatures: zod.array(zod.string()).optional(),
    documentationUrl: zod.string().nullable().optional(),
    description: zod.string().nullable().optional(),
    numberOfBuilds: zod.number().optional(),
    created: zod.string().optional(),
    metadata: zod.record(zod.string(), zod.unknown()).nullable().optional(),
});

function getFirstErrorMessage(parseError: string | null, errors: ValidationErrors[]) {
    if (parseError) {
        return parseError;
    }
    if (!errors.length) {
        return null;
    }

    return `${errors[0].path}: ${errors[0].message}`;
}

function extractKeysFromObject(obj: unknown, prefix = ""): string[] {
    if (typeof obj !== "object" || obj === null) {
        return prefix ? [prefix] : [];
    }

    if (Array.isArray(obj)) {
        if (obj.length === 0) {
            return [];
        }

        return extractKeysFromObject(obj[0], prefix);
    }

    return Object.entries(obj).flatMap(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        return extractKeysFromObject(value, fullKey);
    });
}
const mapKeyWildcard = "*";

function matchesPattern(key: string, pattern: string): boolean {
    const keySegments = key.split(".");
    const patternSegments = pattern.split(".");

    if (keySegments.length !== patternSegments.length) {
        return false;
    }

    return patternSegments.every((segment, index) => segment === mapKeyWildcard || segment === keySegments[index]);
}
function getUnknownKeys(keys: string[], _knownKeys: string[]): string[] {
    const exactKeys = new Set(_knownKeys.filter((key) => !key.includes(mapKeyWildcard)));
    const patterns = _knownKeys.filter((key) => key.includes(mapKeyWildcard));

    return Array.from(new Set(keys)).filter(
        (item) => !exactKeys.has(item) && !patterns.some((pattern) => matchesPattern(item, pattern)),
    );
}

export type ValidationErrors = {
    path: string;
    message: string;
};
type ErrorNode = {
    errors?: string[];
    properties?: Record<string, ErrorNode | undefined>;
    items?: ErrorNode[];
    [key: string]: unknown;
};
function extractErrors(obj: ErrorNode, path: string = ""): ValidationErrors[] {
    const out: ValidationErrors[] = [];

    if (Array.isArray(obj.errors)) {
        obj.errors.forEach((message) => out.push({ path, message }));
    }

    if (obj.properties) {
        for (const [key, val] of Object.entries(obj.properties)) {
            if (!val) {
                continue;
            }
            const newPath = path ? `${path}.${key}` : key;
            out.push(...extractErrors(val, newPath));
        }
    }

    if (Array.isArray(obj.items)) {
        obj.items.forEach((item, index) => {
            if (!item) {
                return;
            }
            const newPath = `${path}[${index}]`;
            out.push(...extractErrors(item, newPath));
        });
    }

    return out;
}
function getErrors(values: Product): ValidationErrors[] {
    const result = productSchema.safeParse(values);

    if (!result.success) {
        const tree = zod.treeifyError(result.error);
        return extractErrors(tree);
    }

    return [];
}

function extractKeysFromSchema(schema: ZodTypeAny, prefix = ""): string[] {
    if (schema instanceof ZodOptional) {
        return extractKeysFromSchema(schema.def.innerType as ZodTypeAny, prefix);
    }

    if (schema instanceof ZodNullable) {
        const innerKeys = extractKeysFromSchema(schema.def.innerType as ZodTypeAny, prefix);
        return prefix && !innerKeys.includes(prefix) ? [prefix, ...innerKeys] : innerKeys;
    }

    if (schema instanceof ZodIntersection) {
        return [
            ...extractKeysFromSchema(schema.def.left as ZodTypeAny, prefix),
            ...extractKeysFromSchema(schema.def.right as ZodTypeAny, prefix),
        ];
    }

    if (schema instanceof ZodObject) {
        return Object.entries(schema.shape).flatMap(([key, child]) => {
            const fullKey = prefix ? `${prefix}.${key}` : key;
            return extractKeysFromSchema(child as ZodTypeAny, fullKey);
        });
    }

    if (schema instanceof ZodRecord) {
        const valueType = schema.def.valueType as ZodTypeAny;
        if (valueType instanceof ZodUnknown || valueType instanceof ZodAny) {
            return prefix ? [prefix] : [];
        }

        return extractKeysFromSchema(valueType, prefix ? `${prefix}.${mapKeyWildcard}` : mapKeyWildcard);
    }

    if (schema instanceof ZodArray) {
        const elementType = schema.def.element as ZodTypeAny;
        return elementType instanceof ZodUnion
            ? elementType.def.options.flatMap((option) => extractKeysFromSchema(option as ZodTypeAny, prefix))
            : extractKeysFromSchema(elementType, prefix);
    }

    if (schema instanceof ZodUnion) {
        return schema.def.options.flatMap((option) => extractKeysFromSchema(option as ZodTypeAny, prefix));
    }

    return prefix ? [prefix] : [];
}

const hubOwnedKeys = [
    "customBuildConfig.modules.tags",
    "customBuildConfig.uiSettings.headlessMode",
    "customBuildConfig.uiSettings.kioskMode",
];
const knownKeys = [...extractKeysFromSchema(productSchema), ...hubOwnedKeys];

const freeFormKeys = ["changes", "metadata"];
function getWarnings(values: Product): string[] {
    const keys = extractKeysFromObject(values);
    return getUnknownKeys(keys, knownKeys).filter((key) => !freeFormKeys.some((prefix) => key.startsWith(`${prefix}.`)));
}

export default {
    extractKeysFromObject,
    extractKeysFromSchema,
    getErrors,
    getFirstErrorMessage,
    getUnknownKeys,
    getWarnings,
    productSchema,
};
